<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :showProviderFilter="isAdmin"
  >
    <ReportTable minWidth="1340px">
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th><span>All appts</span></th>
          <th><span>Completed appts</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Primary appts"
              :tooltip="tooltips['Primary appts']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Patients seen"
              :tooltip="tooltips['Patients seen']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Fluoride %"
              :tooltip="tooltips['Fluoride percentage']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Perio %"
              :tooltip="tooltips['Perio percentage']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="reAppointment %"
              :tooltip="tooltips['reAppointment rate']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Trust score"
              :tooltip="tooltips['Trust score']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Efficiciency score"
              :tooltip="tooltips['Efficiciency score']"
            />
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <!-- clientAdmin view -->
        <template v-if="isAdmin && patientsPerProvider.length">
          <tr
            v-for="(item, index) in patientsPerProvider"
            :key="index"
            :class="{
              active: item.isCurrentUserRecord,
              median: item.isMedian,
              hidden: !item.isActive || item.isHidden || item.isHiddenReport,
            }"
          >
            <td>{{ item.fullName }}</td>
            <td>{{ item.allAppointments }}</td>
            <td>{{ item.completedAppointments }}</td>
            <td>{{ item.primaryCompletedAppointments }}</td>
            <td>{{ item.uniquePatientsByCompletedAppointments }}</td>
            <td>{{ formatPercentage(item.fluoridePercentage) }}</td>
            <td>{{ formatPercentage(item.perioPercentage) }}</td>
            <td>{{ formatPercentage(item.reAppointmentRate) }}</td>
            <td>{{ formatPercentage(item.trustScore) }}</td>
            <td>{{ formatPercentage(item.efficiencyScore) }}</td>
          </tr>

          <tr
            v-if="!userHasRecordInReport"
            class="median"
          >
            <td>Median</td>
            <td>{{ median.allAppointments }}</td>
            <td>{{ median.completedAppointments }}</td>
            <td>{{ median.primaryCompletedAppointments }}</td>
            <td>{{ median.uniquePatientsByCompletedAppointments }}</td>
            <td>{{ formatPercentage(median.fluoridePercentage) }}</td>
            <td>{{ formatPercentage(median.perioPercentage) }}</td>
            <td>{{ formatPercentage(median.reAppointmentRate) }}</td>
            <td>{{ formatPercentage(median.trustScore) }}</td>
            <td>{{ formatPercentage(median.efficiencyScore) }}</td>
          </tr>
        </template>

        <!-- clientUser view -->
        <template v-else-if="!isAdmin && userRecord">
          <tr class="active">
            <td>{{ userRecord.fullName }}</td>
            <td>{{ userRecord.allAppointments }}</td>
            <td>{{ userRecord.completedAppointments }}</td>
            <td>{{ userRecord.primaryCompletedAppointments }}</td>
            <td>{{ userRecord.uniquePatientsByCompletedAppointments }}</td>
            <td>{{ formatPercentage(userRecord.fluoridePercentage) }}</td>
            <td>{{ formatPercentage(userRecord.perioPercentage) }}</td>
            <td>{{ formatPercentage(userRecord.reAppointmentRate) }}</td>
            <td>{{ formatPercentage(userRecord.trustScore) }}</td>
            <td>{{ formatPercentage(userRecord.efficiencyScore) }}</td>
          </tr>

          <tr v-if="median">
            <td>Median</td>
            <td>{{ median.allAppointments }}</td>
            <td>{{ median.completedAppointments }}</td>
            <td>{{ median.primaryCompletedAppointments }}</td>
            <td>{{ median.uniquePatientsByCompletedAppointments }}</td>
            <td>{{ formatPercentage(median.fluoridePercentage) }}</td>
            <td>{{ formatPercentage(median.perioPercentage) }}</td>
            <td>{{ formatPercentage(median.reAppointmentRate) }}</td>
            <td>{{ formatPercentage(median.trustScore) }}</td>
            <td>{{ formatPercentage(median.efficiencyScore) }}</td>
          </tr>

          <tr v-if="averageTop10Percent">
            <td>Average top 10 percent</td>
            <td>{{ averageTop10Percent.allAppointments }}</td>
            <td>{{ averageTop10Percent.completedAppointments }}</td>
            <td>{{ averageTop10Percent.primaryCompletedAppointments }}</td>
            <td>{{ averageTop10Percent.uniquePatientsByCompletedAppointments }}</td>
            <td>{{ formatPercentage(averageTop10Percent.fluoridePercentage) }}</td>
            <td>{{ formatPercentage(averageTop10Percent.perioPercentage) }}</td>
            <td>{{ formatPercentage(averageTop10Percent.reAppointmentRate) }}</td>
            <td>{{ formatPercentage(averageTop10Percent.trustScore) }}</td>
            <td>{{ formatPercentage(averageTop10Percent.efficiencyScore) }}</td>
          </tr>
        </template>

        <NoRecordsFound
          v-else
          colspan="10"
        />
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'RDHPatients',
  components: {
    MetricDefinitionList,
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      patientsPerProviderAll: [],
      userRole: '',
      userRecord: null,
      averageTop10Percent: null,
      median: null,
      loading: false,
      tooltips: {
        'Primary appts': 'Completed appointments whose main/primary provider is the current RDH',
        'Patients seen': 'Amount of unique patients seen in completed appointments',
        'Fluoride percentage': 'Percentage of patients who had a fluoride treatment delivered',
        'Perio percentage':
          'Percentage of patients who were treated by some periodontal code [S/RP, scaling in presence of inflammation or D4910 recall] vs prophy code',
        'reAppointment rate': 'Percentage of patients seen who make a recall appointment',
        'Trust score':
          'Percentage of patients accepting treatments recommended by RDHes. The treatments are done by DDSes',
        'Efficiciency score':
          'Percentage of dollars recommended by RDHes which make it onto the schedule to be completed by DDSes',
      },
    };
  },
  computed: {
    isAdmin() {
      return this.userRole === 'clientAdmin';
    },
    userHasRecordInReport() {
      return !!this.userRecord;
    },
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    patientsPerProvider() {
      return this.showHiddenProviders
        ? this.patientsPerProviderAll
        : this.patientsPerProviderAll.filter(this.providerIsVisible);
    },
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getRDHPatientsReport({ start, end });

      if (result.status === 200) {
        const patientsPerProvider = result.data.patientsPerProvider ?? [];
        this.patientsPerProviderAll = patientsPerProvider;

        Utils.prependCurrentUserRecordAndMedian(patientsPerProvider, result.data.median);

        this.userRecord = result.data.userRecord;
        this.averageTop10Percent = result.data.averageTop10Percent;
        this.median = result.data.median;
      } else {
        this.patientsPerProviderAll = [];
      }

      this.loading = false;
    },
    formatPercentage(number) {
      return Utils.formatPercentage(number);
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
  },
  mounted() {
    this.userRole = DataService.getRole();
  },
};
</script>

<style scoped>
p {
  margin: 0;
}

th,
td {
  padding: 8px 16px !important;
}
</style>
